import React from 'react';
import { Check } from 'lucide-react';
import type { CheckoutPlanId, PricingPlan } from '../../data/pricingPlans';
interface PricingCardProps {
  plan: PricingPlan;
  /** Frontend checkout action — no payment logic lives in this component. */
  onCheckout?: (plan: CheckoutPlanId) => void;
  isLoading?: boolean;
  disabled?: boolean;
  /** Used for the free plan, which links into signup instead of checkout. */
  href?: string;
  /**
   * row    — wide entry tier, content laid out horizontally on desktop
   * column — standard vertical plan card
   */
  layout?: 'row' | 'column';
}
const ACCENT = {
  neutral: {
    name: 'text-white',
    check: 'text-gray-500',
    cadence: 'text-gray-500',
    badge: 'bg-gray-800 text-gray-300 border border-gray-700',
    savings: 'border-gray-700 bg-gray-900 text-gray-300',
    cta: 'bg-gray-800 border border-gray-700 text-white hover:bg-gray-700'
  },
  gold: {
    name: 'text-[#FFD700]',
    check: 'text-[#FFD700]',
    cadence: 'text-gray-400',
    badge: 'bg-[#FFD700] text-black',
    savings: 'border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700]',
    cta: 'bg-[#FFD700] text-black hover:bg-[#F5C400]'
  },
  red: {
    name: 'text-[#FF3B30]',
    check: 'text-[#FF3B30]',
    cadence: 'text-gray-400',
    badge: 'bg-[#FF0000] text-white',
    savings: 'border-[#FF0000]/40 bg-[#FF0000]/10 text-[#FF6B60]',
    cta: 'bg-[#FF0000] text-white hover:bg-[#D90000]'
  }
} as const;
const FRAME = {
  lead: 'border border-gray-800',
  standard: 'border border-gray-800',
  secondary: 'border-2 border-[#FFD700] shadow-[0_0_28px_rgba(255,215,0,0.12)]',
  primary: 'border-2 border-[#FF0000] shadow-[0_0_40px_rgba(255,0,0,0.18)]'
} as const;
/** The recommended plan sits lifted out of the row on wide screens. */
const LIFT = {
  lead: 'hover:-translate-y-1',
  standard: 'hover:-translate-y-1',
  secondary: 'hover:-translate-y-1',
  primary: 'hover:-translate-y-1 lg:-translate-y-3 lg:hover:-translate-y-4'
} as const;
export function PricingCard({
  plan,
  onCheckout,
  isLoading = false,
  disabled = false,
  href,
  layout = 'column'
}: PricingCardProps) {
  const accent = ACCENT[plan.accent];
  const isRow = layout === 'row';
  const ctaClasses = `flex w-full items-center justify-center gap-2 rounded-xl px-5 py-4 text-sm font-black uppercase tracking-wider transition-colors duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505] disabled:cursor-not-allowed disabled:opacity-60 ${accent.cta}`;
  const priceBlock = <div data-id="element-1670" data-mp="2csadmccj3uoj">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-id="element-1671" data-mp="2cs0oeqzb5rjw">
        <span className="text-4xl font-black leading-none tracking-tight text-white" data-id="element-1672" data-mp="1z0cfsxo9pl7o">
          {plan.price}
        </span>
        {plan.originalPrice && <span className="text-base font-bold leading-none text-gray-600 line-through" data-id="element-1673" data-mp="1p1538cpcxulm">
            {plan.originalPrice}
          </span>}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1674" data-mp="1tzokhzt0ai7n">
        {plan.savings && <span className={`rounded-md border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.12em] ${accent.savings}`} data-id="element-1675" data-mp="1aey6zrg6qvwn">
            {plan.savings}
          </span>}
        <span className={`font-mono text-[11px] uppercase tracking-[0.15em] ${accent.cadence}`} data-id="element-1676" data-mp="1kdx7gmqsrl8k">
          {plan.cadence}
        </span>
      </div>
    </div>;
  const nameBlock = <div className="flex items-center gap-2" data-id="element-1677" data-mp="1tbh6rq53n7gj">
      <span aria-hidden="true" className="text-base leading-none" data-id="element-1678" data-mp="1oeqvrh85j21z">
        {plan.glyph}
      </span>
      <h3 className={`text-lg font-black uppercase tracking-wide ${accent.name}`} data-id="element-1679" data-mp="233n2nayh2910">
        {plan.name}
      </h3>
    </div>;
  const cta = href ? <a href={href} className={ctaClasses} data-id="element-1680" data-mp="1y6d332hdcgzg">
      {plan.cta}
    </a> : <button type="button" onClick={() => onCheckout?.(plan.id as CheckoutPlanId)} disabled={disabled || isLoading} aria-busy={isLoading} className={ctaClasses} data-id="element-1681" data-mp="2c41nyabfro4i">
      {isLoading ? <>
          <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" data-id="element-1682" data-mp="aembqlpxzpap" />
          Processing…
        </> : <>
          {plan.cta}
          <span className="sr-only" data-id="element-1683" data-mp="k3dzsirvtpgc"> — {plan.price}</span>
        </>}
    </button>;
  if (isRow) {
    return <article className={`flex h-full flex-col gap-6 rounded-2xl bg-[#0A0A0A] p-6 transition-[transform,border-color] duration-200 ease-out sm:flex-row sm:items-center sm:justify-between ${LIFT[plan.emphasis]} ${FRAME[plan.emphasis]}`} data-id="element-1684" data-mp="3inmbysto9okh">
        <div className="min-w-0" data-id="element-1685" data-mp="gy630qssxdr">
          {nameBlock}
          <div className="mt-4" data-id="element-1686" data-mp="ebwywo9e9tf">{priceBlock}</div>
          <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2" data-id="element-1687" data-mp="17ursipty3n1u">
            {plan.features.map(feature => <li key={feature} className="flex items-center gap-2 text-sm text-gray-400" data-id="element-1688" data-mp="1buk6ji23ylel">
                <Check size={14} strokeWidth={3} aria-hidden="true" className={`shrink-0 ${accent.check}`} data-id="element-1689" data-mp="2wfqikkklg9xv" />
                {feature}
              </li>)}
          </ul>
        </div>
        <div className="w-full shrink-0 sm:w-44" data-id="element-1690" data-mp="2ilps0e3spt8l">{cta}</div>
      </article>;
  }
  return <article className={`flex h-full flex-col rounded-2xl bg-[#0A0A0A] p-6 transition-[transform,border-color] duration-200 ease-out ${LIFT[plan.emphasis]} ${FRAME[plan.emphasis]}`} data-id="element-1691" data-mp="2dopmq5ersuij">
      {/* Badge slot — fixed height so every card header sits on the same baseline */}
      <div className="mb-4 flex h-6 items-start" data-id="element-1692" data-mp="1zoevv2dgnfxd">
        {plan.badge && <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.15em] ${accent.badge}`} data-id="element-1693" data-mp="2vmtije7w3sx">
            {plan.badge}
          </span>}
      </div>

      <div className="mb-5" data-id="element-1694" data-mp="h0dc8gk6zv7d">{nameBlock}</div>

      {priceBlock}

      {/* Note slot — reserved so feature lists across the row stay aligned */}
      <div className="mt-5 min-h-[64px]" data-id="element-1695" data-mp="ltg9ozs3bosu">
        {plan.note && <p className="rounded-lg border border-[#FFD700]/30 bg-[#FFD700]/5 px-3 py-2 text-xs font-medium leading-relaxed text-[#FFD700]" data-id="element-1696" data-mp="818fnfs6ybxu">
            {plan.note}
          </p>}
      </div>

      <ul className="mb-7 mt-5 flex-1 space-y-3" data-id="element-1697" data-mp="10pxk9osggzec">
        {plan.features.map(feature => <li key={feature} className="flex items-start gap-2.5" data-id="element-1698" data-mp="14p6e0tnhwx9w">
            <Check size={15} strokeWidth={3} aria-hidden="true" className={`mt-0.5 shrink-0 ${accent.check}`} data-id="element-1699" data-mp="qqos91h5huum" />
            <span className="text-sm leading-snug text-gray-300" data-id="element-1700" data-mp="vrck1pd7efpw">
              {feature}
            </span>
          </li>)}
      </ul>

      <div className="mt-auto" data-id="element-1701" data-mp="3ggs503pqra7m">{cta}</div>
    </article>;
}